import { apiClient, type SubmissionOptions } from './api-client';
import {
  collectionPlaceField,
  PLACE_REQUIRED_HEADERS,
  type CollectionPlace,
} from '@/features/collection-place/collection-place.contract';
import type { RecordingDeviceCategory, RecordingMicrophoneType } from '@/features/recording-device-metadata';

/** Form fields of POST /audio/upload, besides the file. */
export type WordAudioUploadData = {
  lang: string;
  phraseLocal?: string;
  phraseFr?: string;
  recordingDurationMs?: number;
  communityProfileId?: string | number;
  communityCode?: string;
  collectionPlace?: CollectionPlace | null;
  recordingDeviceType?: string;
  recordingDeviceCategory?: RecordingDeviceCategory;
  recordingMicrophoneType?: RecordingMicrophoneType;
  recordingDeviceDetails?: string;
  /** Phrase index UUID: the server reads the phrase sector from it. */
  phraseId?: string;
};

export const audioApi = {
  async uploadWordAudio(file: File, data: WordAudioUploadData, options: SubmissionOptions = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('lang', data.lang);

    if (data.phraseLocal) {
      formData.append('phraseLocal', data.phraseLocal);
    }
    if (data.phraseFr) {
      formData.append('phraseFr', data.phraseFr);
    }
    if (data.recordingDurationMs !== undefined) {
      formData.append('recordingDurationMs', data.recordingDurationMs.toString());
    }
    if (data.communityProfileId !== undefined && data.communityProfileId !== null) {
      formData.append('communityProfileId', data.communityProfileId.toString());
    }
    if (data.communityCode) {
      formData.append('communityCode', data.communityCode);
    }
    if (data.collectionPlace) {
      formData.append('collectionPlace', collectionPlaceField(data.collectionPlace));
    }
    if (data.recordingDeviceType) formData.append('recordingDeviceType', data.recordingDeviceType);
    if (data.recordingDeviceCategory) formData.append('recordingDeviceCategory', data.recordingDeviceCategory);
    if (data.recordingMicrophoneType) formData.append('recordingMicrophoneType', data.recordingMicrophoneType);
    if (data.recordingDeviceDetails) formData.append('recordingDeviceDetails', data.recordingDeviceDetails);
    if (data.phraseId) {
      formData.append('phraseId', data.phraseId);
    }

    const res = await apiClient.post('/audio/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...PLACE_REQUIRED_HEADERS, ...options.headers },
      skipSessionInvalidation: options.background,
    });
    return res.data;
  },

  // Moderator-only: permanently deletes an audio blob (also allowed for owning contributor/collecting moderator)
  deleteAudio: async (blobName: string) => {
    const { data } = await apiClient.delete('/audio', { params: { blobName } });
    return data;
  },

  /**
   * G2 PCA — contenu audio via l'API authentifiee, et non via une URL de
   * stockage directe. La decision d'acces (proprietaire, collecteur, ou admin
   * dans son perimetre de langue) est appliquee cote serveur : le client ne
   * recoit ni bucket ni jeton signe. C'est ce qui permet de basculer le
   * stockage vers MinIO prive sans rendre aucun bucket public.
   *
   * Le nom du blob est encode : il peut contenir des espaces ou des accents.
   */
  getPrivateAudioContent: async (blobName: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      `/audio/${encodeURIComponent(blobName)}/content`,
      { responseType: 'blob' },
    );
    return data;
  },
};
